// The tools on the Tools page, shared with the quick find. Most are their own apps, so links
// to them load the page instead of navigating inside this app.

import routes from '@config/routes';
import type { Tool } from '@/types';

export type ToolGroup = 'open' | 'sparql' | 'confidential';

export type ToolConfig = Omit<Tool, 'name' | 'description'> & {
  group: ToolGroup;
  /** i18n key of the name */
  nameKey: string;
  /** i18n key of the description */
  descriptionKey: string;
  /** i18n key of other words the quick find finds it by, e.g. a former name */
  keywordsKey?: string;
};

const tools: ToolConfig[] = [
  { group: 'open', id: 'yasgui', nameKey: 'tools.yasgui', descriptionKey: 'tools.yasguiDesc', url: '/yasgui/', icon: '💻', requiredAccessLevel: 'authenticated' },
  { group: 'open', id: 'restApi', nameKey: 'tools.restApi', descriptionKey: 'tools.restApiDesc', url: '/api-portal', icon: '🔌', requiredAccessLevel: 'authenticated' },
  { group: 'open', id: 'explorer', nameKey: 'tools.explorer', descriptionKey: 'tools.explorerDesc', url: '/explorer/', icon: '🗺️', requiredAccessLevel: 'authenticated' },
  // { group: 'open', id: 'data-treehouse-validation', nameKey: 'tools.dataTreehouseValidation', descriptionKey: 'tools.dataTreehouseValidationDesc', keywordsKey: 'tools.dataTreehouseValidationKeywords', url: routes.dataTreehouseValidation, icon: '🌳', requiredAccessLevel: 'authenticated' },

  // These use 'copy' instead of 'open', so users copy the URL for their SPARQL clients
  { group: 'sparql', id: 'sparql-public', nameKey: 'tools.sparqlPublic', descriptionKey: 'tools.sparqlPublicDesc', url: '/sparql', icon: '🔓', requiredAccessLevel: 'authenticated', actionType: 'copy' },
  { group: 'sparql', id: 'sparql-metadata', nameKey: 'tools.sparqlMetadata', descriptionKey: 'tools.sparqlMetadataDesc', url: '/sparql-metadata', icon: '📋', requiredAccessLevel: 'authenticated', actionType: 'copy' },
  { group: 'sparql', id: 'private-sparql-endpoint', nameKey: 'tools.privateSparqlEndpoint', descriptionKey: 'tools.privateSparqlEndpointDesc', url: '/jena-fuseki/dim/', icon: '🔐', requiredAccessLevel: 'projectMember', actionType: 'copy' },
  { group: 'sparql', id: 'sparql-anything', nameKey: 'tools.sparqlAnything', descriptionKey: 'tools.sparqlAnythingDesc', url: '/sparql-anything', icon: '✨', requiredAccessLevel: 'projectMember', actionType: 'copy' },

  { group: 'confidential', id: 'file-browser', nameKey: 'tools.fileBrowser', descriptionKey: 'tools.fileBrowserDesc', url: '/filebrowser/', icon: '📁', requiredAccessLevel: 'projectMember', status: 'available' },
  { group: 'confidential', id: 'jena-fuseki', nameKey: 'tools.jenaFuseki', descriptionKey: 'tools.jenaFusekiDesc', url: '/jena-fuseki/', icon: '🗄️', requiredAccessLevel: 'projectMember', status: 'available' },
  {
    group: 'confidential',
    id: 'airflow',
    nameKey: 'tools.airflow',
    descriptionKey: 'tools.airflowDesc',
    environments: [
      { name: 'Dev', url: 'https://airflow-dev.review.apps.banenor.no/' },
      { name: 'Prod', url: 'https://airflow.apps.banenor.no/' },
    ],
    icon: '🌊',
    requiredAccessLevel: 'projectMember',
    status: 'available',
  },
  { group: 'confidential', id: 'compare', nameKey: 'tools.compare', descriptionKey: 'tools.compareDesc', url: '/compare/', icon: '⚖️', requiredAccessLevel: 'projectMember', status: 'available' },
  { group: 'confidential', id: 'tps-topo-matching', nameKey: 'tools.tpsTopoMatching', descriptionKey: 'tools.tpsTopoMatchingDesc', url: '/tps/', icon: '🧩', requiredAccessLevel: 'projectMember', status: 'available' },
];

/** i18n key of the heading each group has on the Tools page */
export const toolGroupLabelKeys: Record<ToolGroup, string> = {
  open: 'tools.title',
  sparql: 'tools.sparqlEndpoints',
  confidential: 'tools.confidentialTools',
};

/** Id of each group's section on the Tools page, so the quick find can link to it */
export const toolGroupAnchors: Record<ToolGroup, string> = {
  open: 'tools',
  sparql: 'sparql-endpoints',
  confidential: 'admin-tools',
};

/** Where the quick find goes: the tool itself, or its group on the Tools page when the URL is copied or there are several. */
export const toolLink = (tool: ToolConfig): string =>
  tool.url && tool.actionType !== 'copy' ? tool.url : `${routes.tools}#${toolGroupAnchors[tool.group]}`;

/** Whether the path is one of the tools or the data quality portal, which are served by their own apps. */
export const isToolPath = (path: string): boolean =>
  path.startsWith(routes.dataQualityPortal) ||
  tools.some((tool) => tool.url && tool.actionType !== 'copy' && path.startsWith(tool.url));

export default tools;
